import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import {
  DetectedTray,
  ReleaseToDeconStep,
  ReleaseTrayStatus,
} from '../types/release-to-decon';
import cartIllustration from '../../../assets/images/cart-Illustration.svg';
import {
  cancelSelectionButtonSx,
  discoveryActionsSx,
  discoveryCardSx,
  discoveryCardWrapSx,
  discoveryCartIllustrationSx,
  discoveryContainerSx,
  discoveryDetectedListSx,
  discoveryDetectedTitleSx,
  discoveryDetectedTrayBadgeSx,
  discoveryDetectedTrayCheckWrapSx,
  discoveryDetectedTrayFailedTextSx,
  discoveryDetectedTrayDetailsSx,
discoveryDetectedTrayRowSx,
  discoveryDetectedTrayStatusTextSx,
  discoveryDetectedWrapSx,
  discoveryHeaderSx,
  discoveryHeaderTitleStackSx,
  discoveryHeaderWrapSx,
  discoveryPanelSx,
  discoveryStatusLabelSx,
  discoveryStatusRowSx,
  discoveryStatusTitleSx,
  discoveryTitleSx,
  discoveryWaitingCopySx,
  selectedFlowLocationSx,
  selectedFlowTitleSx,
} from './styles';
import { LocationEntity } from '@peritasai/repository';
import { periverseTheme } from '@peritasai/ui-shared';

type DiscoveryCardProps = {
  step: ReleaseToDeconStep;
  selectedLocation: LocationEntity;
  detectedTrays: DetectedTray[];
  showFlowTitle: boolean;
  showCancel: boolean;
  onCancel: () => void;
};

export const DiscoveryCard = ({
  step,
  selectedLocation,
  detectedTrays,
  showFlowTitle,
  showCancel,
  onCancel,
}: DiscoveryCardProps) => (
  <Box sx={discoveryContainerSx}>
    <Box sx={discoveryHeaderWrapSx}>
      <Box
        sx={{
          ...discoveryHeaderSx,
          ...(showCancel ? { alignItems: 'flex-end' } : {}),
        }}
      >
        <Box sx={discoveryHeaderTitleStackSx}>
          {showFlowTitle ? (
            <>
              <Typography sx={selectedFlowTitleSx}>Release to Decon</Typography>
              <Typography sx={selectedFlowLocationSx}>{selectedLocation.name}</Typography>
            </>
          ) : (
            <Typography sx={discoveryTitleSx}>
              {selectedLocation.name}
            </Typography>
          )}
        </Box>
        {showCancel && (
          <Box sx={discoveryActionsSx}>
            <Button
              variant="outlined"
              size="large"
              onClick={onCancel}
              sx={cancelSelectionButtonSx}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
    </Box>
    <Box sx={discoveryCardWrapSx}>
      <Paper sx={discoveryCardSx}>
      {detectedTrays.length <= 0 ? (
        <Box sx={discoveryPanelSx}>
          <Box
            component="img"
            src={cartIllustration}
            alt="Cart illustration"
            sx={discoveryCartIllustrationSx}
          />
          <Box sx={discoveryWaitingCopySx}>
            <Typography sx={discoveryStatusTitleSx}>
              Move Trays onto Station to Release Trays
            </Typography>
            <Box sx={discoveryStatusRowSx}>
              <Typography sx={discoveryStatusLabelSx}>Searching for Trays</Typography>
              <CircularProgress size={16} />
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={discoveryDetectedWrapSx}>
          <Typography sx={discoveryDetectedTitleSx}>
            {detectedTrays.filter((tray) => tray.status === ReleaseTrayStatus.RELEASED).length} of{' '}
            {detectedTrays.length} Trays Released
          </Typography>
          <Box sx={discoveryDetectedListSx}>
            {detectedTrays.map((tray) => (
              <Box
                key={tray.id}
                sx={{
                  ...discoveryDetectedTrayRowSx,
                  cursor: 'auto',
                  ...(tray.status === ReleaseTrayStatus.RELEASED
                    ? {
                        backgroundColor: '#E8F5E9',
                        borderWidth: '0px'
                      }
                    : tray.status === ReleaseTrayStatus.FAILED
                      ? {
                          backgroundColor: '#FDECEA',
                          borderWidth: '0px'
                        }
                      : {}),
                }}
              >
                <Box sx={discoveryDetectedTrayDetailsSx}>
                  <Typography
                    sx={{
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontFamily: 'var(--fontFamily, "Helvetica Neue")',
                      fontSize: '14px',
                      fontWeight: 500,
                      lineHeight: '20.02px',
                      letterSpacing: '0.17px',
                      fontFeatureSettings: '"liga" off, "clig" off',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    {tray.name}
                  </Typography>
                  <Box sx={discoveryDetectedTrayBadgeSx}>
                    <Typography
                      sx={{
                        color: periverseTheme.palette.primary.dark,
                        fontFamily: 'var(--fontFamily, "Helvetica Neue")',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '18px',
                        letterSpacing: '0.16px',
                        fontFeatureSettings: '"liga" off, "clig" off',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}
                    >
                      {tray.subtitle} Detected
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="span"
                  sx={discoveryDetectedTrayCheckWrapSx}
                >
                  {tray.status === ReleaseTrayStatus.PENDING && (
                    <CircularProgress size={18} />
                  )}
                  {tray.status === ReleaseTrayStatus.RELEASED && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm-1 15-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9Z"
                          fill="#2E7D32"
                        />
                      </svg>
                      <Typography sx={discoveryDetectedTrayStatusTextSx}>
                        Released
                      </Typography>
                    </>
                  )}
                  {tray.status === ReleaseTrayStatus.NOOP && (
                    <CircularProgress size={18} sx={{ mr: '24px', color: periverseTheme.palette.primary.dark }} />
                  )}
                  {tray.status === ReleaseTrayStatus.FAILED && (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z"
                          fill="#D32F2F"
                        />
                      </svg>
                      <Typography sx={discoveryDetectedTrayFailedTextSx}>
                        Failed
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={discoveryStatusRowSx}>
            <Typography
              sx={{
                color: periverseTheme.palette.primary.dark,
                textAlign: 'center',
                fontFamily: 'var(--fontFamily, "Helvetica Neue")',
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                fontFeatureSettings: '"liga" off, "clig" off',
              }}
            >
              Searching for Trays
            </Typography>
            <CircularProgress size={16} sx={{ color: periverseTheme.palette.primary.dark }} />
          </Box>
        </Box>
      )}
      </Paper>
    </Box>
  </Box>
);
